import TooltipPro from 'epn-ui/umd/dist/components/TooltipPro';

import getPluralForm from '@utils/formatters/getPluralForm';

import useI18n, { LocaleTypes } from '@i18n';
import Line from '../Line';
import LineHeader from '../LineHeader';
import RemaindCounter from '../RemaindCounter';

import { IMotivationPrepared, ILineData } from '../interfaces';

interface ILineWrapperProps {
    preparedData: IMotivationPrepared;
    lineData: ILineData;
    endDate: number;
    handleModal?: () => void;
}

const TARGET_LOCALES = {
    [LocaleTypes.ru]: ['й', 'й', 'й'],
    [LocaleTypes.en]: ['st', 'nd', 'nd'],
};

const LineWrapper: React.FC<ILineWrapperProps> = ({
    preparedData,
    lineData,
    endDate,
    handleModal,
}) => {
    const { t, locale } = useI18n('Statistic');
    const { proportions, goals, progress, currentGmv } = lineData;
    const hasZeroFirstGoal = goals[0] === 0;
    const alignMap =
        goals.length === 3 ? (['left', 'right', 'right'] as const) : (['left', 'right'] as const);
    const getHeaderClassName = (index: number) => {
        if (hasZeroFirstGoal) return '';
        if (goals.length !== 3) return '';
        if (index === 1) return 'lg:justify-end lg:pr-4';
        if (index === 2) return 'lg:justify-end';
        return '';
    };

    const proportionStyle = {
        gridTemplateColumns: proportions
            .map(proportion => `minmax(0, ${proportion.toFixed()}%)`)
            .join(' '),
    };

    const moreInfoToasts = preparedData.goals.map((goal, index) => (
        <div>
            <p className="font-medium">
                {preparedData.currentGmv} / {goal.goalGmv}
            </p>
            <p>
                {t('When completing the {{target}} purpose of the order', {
                    target: `${index + 1}-${getPluralForm(index + 1, TARGET_LOCALES[locale])}`,
                })}
                <br />
                {t('recalculated by coefficient')}&nbsp;
                <span className="font-medium">x{goal.bonusRatio}</span>
            </p>
            {t('Approximate bonus')}&nbsp;<span className="font-medium">{goal.bonusAmount}</span>
        </div>
    ));

    return (
        <>
            <div className="mb-4 grid grid-cols-2 lg:block">
                <div
                    className={`flex flex-col justify-end gap-2 ${
                        hasZeroFirstGoal ? '' : 'lg:grid lg:justify-start lg:gap-0'
                    }`}
                    style={hasZeroFirstGoal ? undefined : proportionStyle}
                >
                    {preparedData.goals.map((goal, index) => (
                        <LineHeader
                            key={goal.level}
                            level={goal.level}
                            goalGmv={goal.goalGmv}
                            completed={goal.completed}
                            showTooltip={currentGmv >= goals[index]}
                            align={hasZeroFirstGoal ? 'left' : alignMap[index]}
                            nowrap={!hasZeroFirstGoal && goals.length === 3}
                            fullNowrap={!hasZeroFirstGoal && goals.length === 3 && index === 1}
                            className={getHeaderClassName(index)}
                        />
                    ))}
                </div>
                <div className="flex items-end ml-auto mr-0 lg:hidden">
                    <RemaindCounter endDate={endDate} handleModal={handleModal} />
                </div>
            </div>
            <div className="grid" style={proportionStyle}>
                {preparedData.goals.map((goal, index) => (
                    <TooltipPro
                        key={goal.level}
                        title={moreInfoToasts[index]}
                        //  @ts-expect-error TODO Нужно поправить пропсы компонента
                        overlayInnerStyle={{ minWidth: '300px' }}
                        placement="topLeft"
                        arrowPointAtCenter
                    >
                        <div className="cursor-help">
                            <Line
                                target={goal.level}
                                progress={progress[index]}
                                isFirst={index === 0}
                                isLast={index === preparedData.goals.length - 1}
                            />
                        </div>
                    </TooltipPro>
                ))}
            </div>
        </>
    );
};

export default LineWrapper;
